import React from 'react';
import {observer} from 'mobx-react';
import {classes, stylesheet} from 'typestyle';
import _ from 'lodash';
import {observable} from 'mobx';
import {AbtestsForChannel, AbTestWithVariants} from '../../stores/ChannelStore';
import {ImportingAbTest, VariantChannelCompare} from '../../stores/CompareStore';
import {I18n, TProvider} from '../../i18n/i18n';
import {BlueColor, LightGreyColor} from '../../styles/colors';
import {LoadingHover} from '../Legend/TermItem';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import moment from 'moment';

interface AbtestFoldoutProps {
    abTestsForChannels: AbtestsForChannel[];
    variantChannelCompare?: TempVariantChannelCompare;
    applyAbtestCompareSettings: (channelName?: string, testId?: string, variantIds?: string[]) => void;
    importAbTest: (channelName: string, testId: string, lastImported?: moment.Moment) => void;
    importingAbTests: ImportingAbTest[];
}

interface TempVariantChannelCompare extends VariantChannelCompare {
    channelName: string;
}

@observer
export default class AbtestFoldout extends React.Component<AbtestFoldoutProps> {
    @observable private tempSelectedVariants?: TempVariantChannelCompare;

    public componentDidMount() {
        this.tempSelectedVariants = this.props.variantChannelCompare;
    }

    private applyVariantChange = (channelName: string, testId: string, variantId: string) => {
        const variantIdFitsIn = () => {
            return this.tempSelectedVariants && this.tempSelectedVariants.channelName === channelName && this.tempSelectedVariants.testId === testId;
        };

        const variantIdAlreadyApplied = () => {
            return this.tempSelectedVariants && this.tempSelectedVariants.variantIds.some((checkedVariantId) => variantId === checkedVariantId);
        };

        if (variantIdFitsIn() && this.tempSelectedVariants) {
            if (variantIdAlreadyApplied()) {
                const updatedVariantIds = this.tempSelectedVariants.variantIds.filter((checkedVariantId) => checkedVariantId !== variantId);
                if (updatedVariantIds.length > 0) {
                    this.tempSelectedVariants.variantIds = updatedVariantIds;
                } else {
                    this.tempSelectedVariants = undefined;
                }
            } else {
                this.tempSelectedVariants.variantIds.push(variantId);
            }
        } else {
            this.tempSelectedVariants = {
                channelName,
                testId,
                variantIds: [variantId],
            };
        }
        this.applyTempVariantIds();
    };

    private applyTempVariantIds = (): void => {
        const {tempSelectedVariants} = this;
        if (!tempSelectedVariants) {
            this.props.applyAbtestCompareSettings();
            return;
        }
        if (tempSelectedVariants.variantIds.length > 1) {
            this.props.applyAbtestCompareSettings(tempSelectedVariants.channelName, tempSelectedVariants.testId, tempSelectedVariants.variantIds);
        } else {
            this.props.applyAbtestCompareSettings();
        }
    };

    private renderVariant = (variantIds: string[], testId: string, channelName: string) => {
        if (variantIds.length === 2) {
            return null;
        }

        const variantIdChecked = (variantId: string): boolean => {
            const {tempSelectedVariants} = this;
            if (!tempSelectedVariants) {
                return false;
            }
            return (
                channelName === tempSelectedVariants.channelName &&
                testId === tempSelectedVariants.testId &&
                tempSelectedVariants.variantIds.some((activevariantId) => activevariantId === variantId)
            );
        };

        return variantIds.map((variantId) => (
            <label className={abTestFoldoutCss.boxLine} key={variantId + testId + channelName}>
                <input
                    type="checkbox"
                    onChange={() => this.applyVariantChange(channelName, testId, variantId)}
                    checked={variantIdChecked(variantId)}
                />
                {variantId}
            </label>
        ));
    };

    private applyFullAbtest = (channelName: string, testId: string, variantIds: string[]) => {
        const alreadyFullyApplied = (): boolean => {
            const {tempSelectedVariants} = this;
            if (!tempSelectedVariants) {
                return false;
            }
            const variantIdsAreEqual = _.isEqual(_.sortBy(variantIds), _.sortBy(tempSelectedVariants.variantIds));
            return tempSelectedVariants.channelName === channelName && tempSelectedVariants.testId === testId && variantIdsAreEqual;
        };

        if (alreadyFullyApplied()) {
            this.tempSelectedVariants = undefined;
        } else {
            this.tempSelectedVariants = {
                channelName,
                testId,
                variantIds,
            };
        }

        this.applyTempVariantIds();
    };

    private showLoadingHover(loading: boolean): React.ReactNode {
        return loading ? <LoadingHover /> : null;
    }

    private renderAbtest = (abTestsWithVariants: AbTestWithVariants[], channelName: string): React.ReactNode => {
        const abTestChecked = (testId: string): boolean => {
            const {tempSelectedVariants} = this;
            if (!tempSelectedVariants) {
                return false;
            }
            return channelName === tempSelectedVariants.channelName && testId === tempSelectedVariants.testId;
        };

        return abTestsWithVariants.map((abTestWithVariants) => {
            const importRunning = this.props.importingAbTests.some(
                (importing) => importing.testId === abTestWithVariants.testId && importing.channelName === channelName
            );
            return (
                <div key={abTestWithVariants.testId + channelName}>
                    <div className={abTestFoldoutCss.testIdLine}>
                        <div
                            className={abTestFoldoutCss.boxLine}
                            onClick={() => this.applyFullAbtest(channelName, abTestWithVariants.testId, abTestWithVariants.variantIds)}>
                            <input className={abTestFoldoutCss.box} type="radio" readOnly={true} checked={abTestChecked(abTestWithVariants.testId)} />
                            {abTestWithVariants.name || abTestWithVariants.testId}
                        </div>
                        <TProvider>
                            {(t) => (
                                <button
                                    title={t('import')}
                                    disabled={importRunning || !abTestWithVariants.importNeeded}
                                    className={classes(abTestFoldoutCss.importButton, !abTestWithVariants.importNeeded ? 'disabled' : undefined)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        this.props.importAbTest(channelName, abTestWithVariants.testId, abTestWithVariants.lastImported);
                                    }}>
                                    <FontAwesomeIcon icon="file-import" />
                                    {this.showLoadingHover(importRunning)}
                                </button>
                            )}
                        </TProvider>
                    </div>
                    <div className={abTestFoldoutCss.leftMargin}>
                        {this.renderVariant(abTestWithVariants.variantIds, abTestWithVariants.testId, channelName)}
                    </div>
                </div>
            );
        });
    };

    private getAbtestVariantComponentsForChannels(): React.ReactNode[] {
        const channelsWithVariants = this.props.abTestsForChannels.filter((abTestsForChannel) => {
            return (
                abTestsForChannel.abTestsWithVariants.length > 0 &&
                abTestsForChannel.abTestsWithVariants.some((abTestWithVariants) => abTestWithVariants.variantIds.length > 0)
            );
        });
        return channelsWithVariants.map((abTestsForChannel) => (
            <div className={abTestFoldoutCss.abTestChannelContainer} key={abTestsForChannel.channelName}>
                <div className={abTestFoldoutCss.channelText}>{abTestsForChannel.channelName}</div>
                <div className={abTestFoldoutCss.leftMargin}>
                    {this.renderAbtest(abTestsForChannel.abTestsWithVariants, abTestsForChannel.channelName)}
                </div>
            </div>
        ));
    }

    public render() {
        const abTestVariantComponentsForChannels = this.getAbtestVariantComponentsForChannels();
        if (abTestVariantComponentsForChannels.length > 0) {
            return abTestVariantComponentsForChannels;
        } else {
            return <I18n i18nKey="abtest:noAbTestsAvailable" />;
        }
    }
}

const abTestFoldoutCss = stylesheet({
    channelText: {
        fontSize: 12,
        fontWeight: 'bold',
    },
    importButton: {
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 2,
        border: 'none',
        color: 'white',
        backgroundColor: BlueColor,
        padding: '5px 0px',
        marginLeft: 5,
        width: 34,
        $nest: {
            '&.disabled': {
                cursor: 'auto',
                background: LightGreyColor,
            },
        },
    },
    boxLine: {
        cursor: 'pointer',
    },
    testIdLine: {
        display: 'flex',
        alignItems: 'center',
    },
    box: {
        marginRight: 5,
    },
    abTestChannelContainer: {
        margin: 5,
    },
    leftMargin: {
        marginLeft: 15,
    },
    noAbtestText: {
        textAlign: 'center',
        alignSelf: 'center',
        width: '100%',
    },
    noAbtestContainer: {
        height: '100%',
        display: 'flex',
        textAlign: 'center',
        alignSelf: 'center',
        color: '#ccc',
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/AbtestFoldout.tsx